import React, { useEffect, useState } from "react";
import {
  Building2, MapPin, Users, LifeBuoy, Wallet, Star, LogIn, Activity,
  TrendingUp, IdCard, ShoppingBag, CreditCard, Nfc, QrCode,
} from "lucide-react";
import api from "../../../lib/api";
import { Label, StatCard, scoreColor } from "../../../components/common";
import { VolumeArea, BarSeries } from "../../../components/charts";

const RANGES = [
  { v: 7, label: "7 days" },
  { v: 30, label: "30 days" },
  { v: 90, label: "90 days" },
];

export default function PlatformOverview() {
  const [days, setDays] = useState(30);
  const [d, setD] = useState(null);
  const [tbl, setTbl] = useState([]);

  useEffect(() => { api.get(`/platform/analytics?days=${days}`).then((r) => setD(r.data)).catch(() => {}); }, [days]);
  useEffect(() => { api.get("/platform/organizations").then((r) => setTbl(r.data)).catch(() => {}); }, []);

  if (!d) return <div className="text-slate-400">Loading platform analytics…</div>;
  const k = d.kpis;
  const ratingDist = d.rating_distribution.map((r) => ({ label: `${r.rating}★`, count: r.count }));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><Label>Servantage Platform</Label><h1 className="font-head text-2xl font-bold text-slate-900 sm:text-3xl">Group Overview</h1></div>
        <div className="flex rounded-lg border border-slate-200 bg-white p-1" data-testid="range-switch">
          {RANGES.map((r) => (
            <button key={r.v} onClick={() => setDays(r.v)} data-testid={`range-${r.v}`}
              className={`rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${days === r.v ? "bg-[#4338CA] text-white" : "text-slate-500 hover:bg-slate-50"}`}>
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* Primary KPIs */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Corporates" value={k.organizations} sub={`+${k.new_organizations} new · ${k.active_corporates} active`} testId="pf-orgs"><Building2 className="h-5 w-5 text-slate-300" /></StatCard>
        <StatCard label="Employees onboarded" value={k.employees.toLocaleString()} sub={`+${k.new_employees} this period`} testId="pf-employees"><Users className="h-5 w-5 text-slate-300" /></StatCard>
        <StatCard label="Ratings collected" value={k.ratings_total.toLocaleString()} sub={k.avg_rating ? `avg ${k.avg_rating}★` : "—"} accent="#4338CA" testId="pf-ratings"><Star className="h-5 w-5 text-slate-300" /></StatCard>
        <StatCard label="MRR" value={`R${k.mrr.toLocaleString()}`} sub={`ARR R${k.arr.toLocaleString()}`} accent="#10B981" testId="pf-mrr"><Wallet className="h-5 w-5 text-slate-300" /></StatCard>
      </div>

      {/* Secondary KPIs */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6">
        <StatCard label="Logins" value={k.logins.toLocaleString()} testId="pf-logins"><LogIn className="h-5 w-5 text-slate-300" /></StatCard>
        <StatCard label="Locations" value={k.locations} testId="pf-locations"><MapPin className="h-5 w-5 text-slate-300" /></StatCard>
        <StatCard label="Verified interactions" value={k.verified_interactions.toLocaleString()} testId="pf-interactions"><Activity className="h-5 w-5 text-slate-300" /></StatCard>
        <StatCard label="NFC provisioned" value={`${k.nfc_pct}%`} testId="pf-nfc"><IdCard className="h-5 w-5 text-slate-300" /></StatCard>
        <StatCard label="Active subs" value={k.active_subscriptions} sub={k.churned_subscriptions ? `${k.churned_subscriptions} churned` : "0 churned"} testId="pf-subs"><CreditCard className="h-5 w-5 text-slate-300" /></StatCard>
        <StatCard label="Merch revenue" value={`R${k.merch_revenue.toLocaleString()}`} testId="pf-merch"><ShoppingBag className="h-5 w-5 text-slate-300" /></StatCard>
      </div>

      {/* Recovery band */}
      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard label="Open recovery cases" value={k.open_recovery} accent="#EF4444" testId="pf-recovery"><LifeBuoy className="h-5 w-5 text-slate-300" /></StatCard>
        <StatCard label="Resolution rate" value={k.resolution_rate != null ? `${k.resolution_rate}%` : "—"} accent="#10B981" testId="pf-resrate" />
        <StatCard label="Avg resolution time" value={k.avg_resolution_hours != null ? `${k.avg_resolution_hours}h` : "—"} testId="pf-restime" />
      </div>

      {/* Trend charts */}
      <div className="grid gap-4 lg:grid-cols-2">
        <ChartCard title="Ratings collected" icon={Star}><VolumeArea data={d.series.ratings} label="Ratings" color="#4338CA" testId="chart-ratings" /></ChartCard>
        <ChartCard title="Logins" icon={LogIn}><VolumeArea data={d.series.logins} label="Logins" color="#0EA5E9" testId="chart-logins" /></ChartCard>
        <ChartCard title="New corporates" icon={Building2}><VolumeArea data={d.series.signups} label="Signups" color="#10B981" testId="chart-signups" /></ChartCard>
        <ChartCard title="Employees onboarded" icon={Users}><VolumeArea data={d.series.employees} label="Employees" color="#F59E0B" testId="chart-employees" /></ChartCard>
      </div>

      {/* Distributions */}
      <div className="grid gap-4 lg:grid-cols-2">
        <ChartCard title="Rating distribution" icon={Star}><BarSeries data={ratingDist} xKey="label" yKey="count" color="#4338CA" testId="chart-rating-dist" height={220} /></ChartCard>
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="flex items-center gap-2"><CreditCard className="h-4 w-4 text-slate-400" /><Label>Tier distribution</Label></div>
          <div className="mt-3 space-y-2">
            {d.tier_distribution.length === 0 ? <p className="py-6 text-center text-sm text-slate-400">No active subscriptions.</p> :
              d.tier_distribution.map((t) => (
                <div key={t.plan} className="flex items-center justify-between rounded-lg bg-slate-50 p-3" data-testid={`tier-${t.plan}`}>
                  <span className="font-semibold text-slate-700">{t.plan}</span>
                  <span className="text-sm text-slate-500">{t.count} corp · <b className="text-slate-700">R{t.mrr.toLocaleString()}/mo</b></span>
                </div>
              ))}
          </div>
        </div>
      </div>

      {/* Scan mix — QR vs NFC adoption */}
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="scan-mix">
          <div className="flex items-center gap-2"><Nfc className="h-4 w-4 text-slate-400" /><Label>Scan mix · QR vs NFC</Label></div>
          <div className="mt-4 flex items-end justify-between">
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-400">NFC adoption</p>
              <p className="sv-metric text-3xl font-bold text-[#4338CA]" data-testid="scan-nfc-pct">{d.scan_mix.nfc_pct}%</p>
              <p className="mt-1 text-sm text-slate-500">{d.scan_mix.nfc.toLocaleString()} NFC · {d.scan_mix.qr.toLocaleString()} QR</p>
            </div>
            <div className="text-right text-sm text-slate-400">
              <p className="flex items-center justify-end gap-1"><Nfc className="h-3.5 w-3.5 text-[#4338CA]" /> NFC taps</p>
              <p className="flex items-center justify-end gap-1"><QrCode className="h-3.5 w-3.5 text-slate-400" /> QR scans</p>
            </div>
          </div>
          <div className="mt-4 flex h-3 overflow-hidden rounded-full bg-slate-100" data-testid="scan-mix-bar">
            <div className="h-full bg-[#4338CA]" style={{ width: `${d.scan_mix.nfc_pct}%` }} />
            <div className="h-full bg-slate-300" style={{ width: `${100 - d.scan_mix.nfc_pct}%` }} />
          </div>
        </div>
        <ChartCard title="NFC taps over time" icon={Nfc}><VolumeArea data={d.series.nfc_scans} label="NFC taps" color="#4338CA" testId="chart-nfc" /></ChartCard>
      </div>

      {/* Industry breakdown */}
      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="flex items-center gap-2"><TrendingUp className="h-4 w-4 text-slate-400" /><Label>Industry breakdown</Label></div>
        <div className="mt-3 overflow-x-auto sv-scroll">
          <table className="w-full min-w-[560px] text-sm" data-testid="industry-table">
            <thead><tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-400">
              <th className="py-2">Industry</th><th>Corporates</th><th>Employees</th><th>Interactions</th><th>Avg Service Index</th></tr></thead>
            <tbody>
              {d.industry_breakdown.map((i) => (
                <tr key={i.industry} className="border-b border-slate-100" data-testid={`industry-${i.industry}`}>
                  <td className="py-3 font-semibold text-slate-800">{i.industry}</td>
                  <td className="text-slate-500">{i.orgs}</td>
                  <td className="text-slate-500">{i.employees}</td>
                  <td className="text-slate-500">{i.interactions}</td>
                  <td><span className="sv-metric font-bold" style={{ color: scoreColor(i.avg_ssi) }}>{i.avg_ssi.toFixed(2)}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ChartCard({ title, icon: Icon, children }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="flex items-center gap-2"><Icon className="h-4 w-4 text-slate-400" /><Label>{title}</Label></div>
      <div className="mt-3">{children}</div>
    </div>
  );
}
